import { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import ChartLegend from "../ui/ChartLegend";

const COLORS = ["#d946ef", "#f97316", "#14b8a6", "#6366f1", "#f43f5e", "#eab308", "#0ea5e9", "#84cc16"];

/** Carta dalam chat AI. spec: {type, title, xKey, series:[{key,name}], data:[...]} */
export default function AIChart({ raw }) {
  const spec = useMemo(() => {
    try {
      const s = JSON.parse(raw);
      if (!Array.isArray(s.data) || !s.data.length) return null;
      const xKey = s.xKey || Object.keys(s.data[0])[0];
      const series = (s.series && s.series.length ? s.series : Object.keys(s.data[0]).filter((k) => k !== xKey && typeof s.data[0][k] === "number").map((k) => ({ key: k, name: k })))
        .slice(0, 6);
      return { ...s, xKey, series, type: (s.type || "bar").toLowerCase(), data: s.data.slice(0, 40) };
    } catch {
      return null;
    }
  }, [raw]);

  if (!spec) {
    return (
      <div className="my-2 rounded-xl border border-dashed border-fuchsia-200 bg-fuchsia-50/40 px-3 py-4 text-xs text-fuchsia-600 flex items-center gap-2" data-testid="ai-chart-loading">
        <BarChart3 className="h-4 w-4 animate-pulse" /> Menyediakan carta...
      </div>
    );
  }

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
      <XAxis dataKey={spec.xKey} tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={spec.data.length > 6 ? -30 : 0} textAnchor={spec.data.length > 6 ? "end" : "middle"} height={spec.data.length > 6 ? 50 : 24} />
      <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={42} />
      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #f5d0fe" }} />
    </>
  );

  const legendItems = spec.type === "pie"
    ? spec.data.map((d, i) => ({ label: String(d[spec.xKey] ?? ""), color: COLORS[i % COLORS.length] }))
    : spec.series.length > 1 ? spec.series.map((s, i) => ({ label: s.name || s.key, color: COLORS[i % COLORS.length], type: spec.type === "line" ? "line" : "square" })) : [];

  let chart;
  if (spec.type === "pie") {
    const k = spec.series[0]?.key;
    chart = (
      <PieChart>
        <Pie data={spec.data} dataKey={k} nameKey={spec.xKey} outerRadius={70} innerRadius={34} paddingAngle={2} label={{ fontSize: 10 }}>
          {spec.data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
      </PieChart>
    );
  } else if (spec.type === "line") {
    chart = <LineChart data={spec.data}>{axes}{spec.series.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} />)}</LineChart>;
  } else if (spec.type === "area") {
    chart = <AreaChart data={spec.data}>{axes}{spec.series.map((s, i) => <Area key={s.key} type="monotone" dataKey={s.key} name={s.name || s.key} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.18} strokeWidth={2} />)}</AreaChart>;
  } else {
    chart = <BarChart data={spec.data}>{axes}{spec.series.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.name || s.key} fill={COLORS[i % COLORS.length]} radius={[6, 6, 0, 0]} maxBarSize={42} />)}</BarChart>;
  }

  return (
    <div className="my-2 rounded-2xl border border-slate-100 bg-gradient-to-b from-white to-fuchsia-50/30 p-2 shadow-sm" data-testid="ai-chart">
      {spec.title && <div className="text-[12px] font-extrabold text-slate-700 px-1 pb-1">{spec.title}</div>}
      <div className="h-52 w-full">
        <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
      </div>
      <ChartLegend fontSize={11} items={legendItems} />
    </div>
  );
}
